import { Combobox } from "@sentricmusic/ui";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useElasticQuery } from "@/api/mutations";

import { indexReducedPattern } from "./constants";

function buildWriterLabel(first, middle, last) {
  return [first, middle, last].filter(Boolean).join(" ");
}

export default function WriterFilter({ accounts, values, onValuesChange }) {
  const { t } = useTranslation("common");
  const { mutate: searchWriters } = useElasticQuery();

  const [writerOptions, setWriterOptions] = useState([]);
  const [isWriterLoading, setIsWriterLoading] = useState(false);
  const hasFetchedRef = useRef(false);
  const debounceRef = useRef(null);

  const fetchWriters = useCallback((accts, search = "") => {
    if (!accts?.length)
      return;

    setIsWriterLoading(true);

    const mustClause = search.trim()
      ? {
          bool: {
            should: [
              { match_phrase_prefix: { writerLastName: { query: search, max_expansions: 50 } } },
              { match_phrase_prefix: { writerFirstName: { query: search, max_expansions: 50 } } },
            ],
          },
        }
      : { match_all: {} };

    searchWriters({
      index: indexReducedPattern,
      body: {
        query: {
          bool: {
            must: mustClause,
            filter: [{ terms: { artistAccountCode: accts } }],
          },
        },
        collapse: { field: "writerCode" },
      },
      size: 50,
    }, {
      onSuccess: (data) => {
        const hits = data?.body?.hits?.hits || [];
        const optionsMap = new Map();

        hits.forEach((hit) => {
          const source = hit?._source || {};
          const writerCode = source.writerCode;
          if (!writerCode || optionsMap.has(writerCode))
            return;

          const label = buildWriterLabel(
            source.writerFirstName,
            source.writerMiddleName,
            source.writerLastName,
          );
          optionsMap.set(writerCode, label || writerCode);
        });

        setWriterOptions(Array.from(optionsMap, ([value, label]) => ({ value, label })));
        setIsWriterLoading(false);
      },
      onError: () => {
        setWriterOptions([]);
        setIsWriterLoading(false);
      },
    });
  }, [searchWriters]);

  // Reset options when accounts cascade
  useEffect(() => {
    hasFetchedRef.current = false;
    setWriterOptions([]);
  }, [accounts]);

  const handleFocus = useCallback(() => {
    if (hasFetchedRef.current || !accounts?.length)
      return;
    hasFetchedRef.current = true;
    fetchWriters(accounts);
  }, [fetchWriters, accounts]);

  const handleWriterSearch = useCallback((search) => {
    if (debounceRef.current)
      clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchWriters(accounts, search);
    }, 500);
  }, [fetchWriters, accounts]);

  useEffect(() => {
    return () => {
      if (debounceRef.current)
        clearTimeout(debounceRef.current);
    };
  }, []);

  return (
    <div onFocus={handleFocus}>
      <Combobox
        customWidth
        triggerClassName="w-full"
        label={t("other:writer", "Writer")}
        placeholder={t("other:select-writer", "Select writer...")}
        data={writerOptions}
        values={values}
        onValuesChange={onValuesChange}
        onSearchChange={handleWriterSearch}
        selectAllLabel={t("common:select-all", "Select all")}
        clearButton
        clearLabel={t("other:clear", "Clear")}
        isLoading={isWriterLoading}
        disabled={!accounts?.length}
      />
    </div>
  );
}
